var _a, _b;
const updaterSet = new Set();
export function addMicrotask(func) {
    if (!updaterSet.size) {
        // delayed execution callback after updating store
        window.queueMicrotask(() => {
            updaterSet.forEach(func => func());
            updaterSet.clear();
        });
    }
    updaterSet.delete(func);
    updaterSet.add(func);
}
/**
 * `EventTarget` safari not support
 * https://bugs.webkit.org/show_bug.cgi?id=174313
 */
export class Pool extends Image {
    constructor() {
        super();
        this.currentId = 0;
        this.count = 0;
        this.pool = new Map();
        // https://bugs.webkit.org/show_bug.cgi?id=198674
        Object.setPrototypeOf(this, Pool.prototype);
    }
    add(item) {
        if (!this.pool.size)
            this.dispatchEvent(new CustomEvent('start'));
        this.pool.set(this.count, item);
        this.count += 1;
    }
    get() {
        const item = this.pool.get(this.currentId);
        if (item) {
            this.pool.delete(this.currentId);
            this.currentId += 1;
            if (!this.pool.size)
                this.dispatchEvent(new CustomEvent('end'));
        }
        return item;
    }
}
var StorageType;
(function (StorageType) {
    StorageType["LOCALSTORAGE"] = "localStorage";
    StorageType["SESSIONSTORAGE"] = "sessionStorage";
})(StorageType || (StorageType = {}));
class StorageCache {
    constructor() {
        this[_a] = {};
        this[_b] = {};
    }
}
_a = StorageType.LOCALSTORAGE, _b = StorageType.SESSIONSTORAGE;
export class Storage {
    constructor() {
        this.cache = new StorageCache();
    }
    get(key, type) {
        if (key in this.cache[type])
            return this.cache[type][key];
        const value = window[type].getItem(key);
        if (!value)
            return undefined;
        try {
            const result = JSON.parse(value);
            this.cache[type][key] = result;
            return result;
        }
        catch (e) {
            window[type].removeItem(key);
        }
    }
    getLocal(key) {
        return this.get(key, StorageType.LOCALSTORAGE);
    }
    getSession(key) {
        return this.get(key, StorageType.SESSIONSTORAGE);
    }
    set(key, value, type) {
        this.cache[type][key] = value;
        return window[type].setItem(key, JSON.stringify(value));
    }
    setLocal(key, value) {
        return this.set(key, value, StorageType.LOCALSTORAGE);
    }
    setSession(key, value) {
        return this.set(key, value, StorageType.SESSIONSTORAGE);
    }
}
export class QueryString extends URLSearchParams {
    constructor(param) {
        if (param instanceof QueryString) {
            return param;
        }
        super(param);
        /**
         * can't extend `URLSearchParams`
         * https://bugs.webkit.org/show_bug.cgi?id=198674
         */
        Object.setPrototypeOf(this, QueryString.prototype);
    }
    concat(param) {
        let query;
        if (typeof param === 'string') {
            query = Object.fromEntries(new URLSearchParams(param).entries());
        }
        else {
            query = param;
        }
        Object.keys(query).forEach(key => {
            this.append(key, query[key]);
        });
    }
    toString() {
        const string = super.toString();
        return string ? `?${string}` : '';
    }
    toJSON() {
        return this.toString();
    }
}
// 写 html 文本
export function raw(arr, ...args) {
    return arr.reduce((prev, current, index) => prev + (args[index - 1] || '') + current);
}
// 写 css 文本，在 CSSStyleSheet 中使用
export function css(arr, ...args) {
    return raw(arr, ...args);
}
const rulesWeakMap = new WeakMap();
/**
 * !!! 目前只有 Chrome 支持
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1520690
 *
 * 创建 style sheet 用于 `adoptedStyleSheets`
 * @param rules string 不能动态更新的 css
 * @param mediaQuery string 媒体查询
 */
export function createCSSSheet(rules, mediaQuery = '') {
    let cssSheet = rulesWeakMap.get(rules);
    if (!cssSheet) {
        const sheet = new CSSStyleSheet();
        sheet.media.mediaText = mediaQuery;
        let style = '';
        if (typeof rules === 'string') {
            style = rules;
        }
        else {
            Object.keys(rules).forEach(key => {
                sheet[key] = rules[key].type === 'tag' ? key : `${key}-${rules[key].key}`;
                style += rules[key].str.replace(/&/g, sheet[key]);
            });
            rulesWeakMap.set(rules, sheet);
        }
        sheet.replaceSync(style);
        cssSheet = sheet;
    }
    return cssSheet;
}
function randomStr(number = 5, origin = '0123456789abcdefghijklmnopqrstuvwxyz') {
    const len = origin.length;
    let str = '';
    for (let i = 0; i < number; i++) {
        str += origin[Math.floor(Math.random() * len)];
    }
    return str;
}
// 只支持一层嵌套
// https://drafts.csswg.org/css-nesting-1/
function flatStyled(style, type) {
    const subStyle = [];
    let str = `& {${style.replace(new RegExp('&.*{(.*)}', 'gs'), function (substr) {
        subStyle.push(substr);
        return '';
    })}}` + subStyle.join('');
    if (type !== 'tag')
        str = str.replace(/&/g, type === 'class' ? '.&' : '#&');
    return { str, key: randomStr(), type };
}
// 写 css 文本，在 CSSStyleSheet 中使用，使用 styed-components 高亮
//
// createCSSSheet({
//   red: styled.class`
//     background: red;
//     &:hover {
//       background: blue;
//     }
//   `,
// });
export const styled = {
    class: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'class');
    },
    id: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'id');
    },
    tag: (arr, ...args) => {
        const style = raw(arr, ...args);
        return flatStyled(style, 'tag');
    },
};
export function camelToKebabCase(str) {
    return str.replace(/[A-Z]/g, ($1) => '-' + $1.toLowerCase());
}
export function kebabToCamelCase(str) {
    return str.replace(/-(.)/g, (_substr, $1) => $1.toUpperCase());
}
export function emptyFunction() {
    // 用于占位的空函数
}
//# sourceMappingURL=utils.js.map